import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth'
import { auth, track } from '../firebase'

type AuthContextValue = {
  user: User | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<void>
  signOutUser: () => Promise<void>
}

function requireAuth() {
  if (!auth) throw new Error('Firebase auth is not configured.')
  return auth
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  // Nothing to wait for when auth isn't configured.
  const [loading, setLoading] = useState(() => Boolean(auth))

  useEffect(() => {
    if (!auth) return
    return onAuthStateChanged(auth, (u) => {
      setUser(u)
      setLoading(false)
    })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async signInWithGoogle() {
        await signInWithPopup(requireAuth(), new GoogleAuthProvider())
        track('login', { method: 'google' })
      },
      async signInWithEmail(email, password) {
        await signInWithEmailAndPassword(requireAuth(), email, password)
        track('login', { method: 'password' })
      },
      async signUpWithEmail(email, password) {
        await createUserWithEmailAndPassword(requireAuth(), email, password)
        track('sign_up', { method: 'password' })
      },
      async signOutUser() {
        await signOut(requireAuth())
      },
    }),
    [user, loading],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
